import React, { useEffect, useMemo, useRef, useState } from 'react';
import { number, string, bool } from 'prop-types';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import {
  OpBox,
  StyledEditButton,
  StyledIconButton,
  StyledLabel,
  StyledLabelWrapper,
  StyledMoreHorizIcon,
  StyledPopover,
  StyledWrapper,
} from './styled';
import { AddConditionModal } from '../Conditions/AddConditionModal';
import { EditConditionModal } from '../Conditions/EditConditionModal';
import { ChooseValueConditionModal } from '../Conditions/ChooseValueConditionModal';
import { useFlowEditorContext } from '../../context';
import THEME from '../../../../constants/theme';
import { getValidOperatorLabel } from '../../utils/getValidOperatorLabel';
import ChooseCustomConditionValue from '../Conditions/ChooseCustomConditionValue';

const editButtonSize = 16;
const CUSTOM_CONDITION = 'Custom Condition';

const EdgeButton = ({ id, edgeCenterX, edgeCenterY, stepId, targetId, sourceHandleId, targetHandleId, edgeError }) => {
  const ref = useRef();
  const { findNodeDataById, setSelectedNodeId } = useFlowEditorContext();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isShowAddConditionModal, setIsShowAddConditionModal] = useState(false);
  const [isShowEditConditionModal, setIsShowEditConditionModal] = useState(false);
  const [isShowChooseValueConditionModal, setIsShowChooseValueConditionModal] = useState(false);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const conditionNodeData = findNodeDataById(stepId);
  const condition = useMemo(() => conditionNodeData?.conditions?.find((c) => c.nextStepId === targetId), [
    conditionNodeData?.conditions,
    targetId,
  ]);
  const isConditionValid = useMemo(() => !!(condition && condition?.op && condition?.right), [condition]);
  const isOtherEdge = useMemo(() => conditionNodeData?.elseStepId === targetId, [
    conditionNodeData?.elseStepId,
    targetId,
  ]);
  const otherLabel = useMemo(() => (conditionNodeData?.conditions?.length ? 'Other' : 'All'), [
    conditionNodeData?.conditions?.length,
  ]);

  useEffect(() => {
    setWidth(ref.current.offsetWidth + (isConditionValid || isOtherEdge ? -editButtonSize : 0));
    setHeight(ref.current.offsetHeight);
  }, [condition, isConditionValid, isOtherEdge, otherLabel]);

  const showAddModalHandler = (e) => {
    setSelectedNodeId(id);
    setAnchorEl(e.currentTarget);
    setIsShowAddConditionModal(true);
  };

  const showEditModalHandler = (e) => {
    setSelectedNodeId(id);
    setAnchorEl(e.currentTarget);
    setIsShowEditConditionModal(true);
  };

  const onClose = (e) => {
    e.stopPropagation();
    setIsShowEditConditionModal(false);
    setIsShowAddConditionModal(false);
  };

  return (
    <foreignObject
      width={width}
      height={height}
      x={edgeCenterX - width / 2}
      y={edgeCenterY - height / 2}
      className="edgebutton-foreignobject"
      requiredExtensions="http://www.w3.org/1999/xhtml"
    >
      <StyledWrapper>
        <Box ref={ref}>
          {isOtherEdge && (
            <StyledLabelWrapper>
              <StyledLabel $color={edgeError ? THEME.secondaryColors.nodeError : THEME.greyColors.grey1}>
                {otherLabel}
              </StyledLabel>
              <StyledEditButton size="small" onClick={(e) => showEditModalHandler(e, id)}>
                <StyledMoreHorizIcon color="inherit" fontSize="small" />
              </StyledEditButton>
            </StyledLabelWrapper>
          )}
          {isConditionValid && !isOtherEdge && (
            <StyledLabelWrapper>
              <StyledLabel $color={edgeError && THEME.secondaryColors.nodeError}>
                {condition?.label?.from && (
                  <Box component="span">
                    {condition.label.from}
                    <br />
                  </Box>
                )}
                <OpBox component="span" textTransform="lowercase">
                  {getValidOperatorLabel(condition?.op)}
                  {condition?.right?.from && <br />}
                </OpBox>
                <Box component="span">
                  {' '}
                  {condition?.right?.from
                    ? condition?.label.to || condition.right.to
                    : condition?.label || condition?.right}
                </Box>
              </StyledLabel>
              <StyledEditButton size="small" onClick={(e) => showEditModalHandler(e, id)}>
                <StyledMoreHorizIcon color="inherit" fontSize="small" />
              </StyledEditButton>
            </StyledLabelWrapper>
          )}
          {!isConditionValid && !isOtherEdge && (
            <StyledIconButton size="small" onClick={(e) => showAddModalHandler(e, id)}>
              <AddIcon color="inherit" fontSize="small" />
            </StyledIconButton>
          )}
        </Box>
        <StyledPopover
          id={id}
          open={isShowChooseValueConditionModal || isShowAddConditionModal || isShowEditConditionModal}
          anchorEl={anchorEl}
          onClose={onClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <AddConditionModal
            id={id}
            stepId={stepId}
            targetId={targetId}
            sourceHandleId={sourceHandleId}
            targetHandleId={targetHandleId}
            isOpen={isShowAddConditionModal}
            setIsShowAddConditionModal={setIsShowAddConditionModal}
            setIsShowChooseValueConditionModal={setIsShowChooseValueConditionModal}
          />
          <EditConditionModal
            id={id}
            stepId={stepId}
            targetId={targetId}
            isOpen={isShowEditConditionModal}
            setIsShowEditConditionModal={setIsShowEditConditionModal}
            setIsShowChooseValueConditionModal={setIsShowChooseValueConditionModal}
          />
          {conditionNodeData?.name === CUSTOM_CONDITION ? (
            <ChooseCustomConditionValue
              isOpen={isShowChooseValueConditionModal}
              setIsShowChooseValueConditionModal={setIsShowChooseValueConditionModal}
            />
          ) : (
            <ChooseValueConditionModal
              isOpen={isShowChooseValueConditionModal}
              stepId={stepId}
              targetId={targetId}
              property={conditionNodeData?.conditionProperty}
              setIsShowChooseValueConditionModal={setIsShowChooseValueConditionModal}
            />
          )}
        </StyledPopover>
      </StyledWrapper>
    </foreignObject>
  );
};

EdgeButton.propTypes = {
  id: string.isRequired,
  edgeCenterX: number,
  edgeCenterY: number,
  stepId: string.isRequired,
  targetId: string,
  sourceHandleId: string,
  targetHandleId: string,
  edgeError: bool,
};

EdgeButton.defaultProps = {
  edgeError: false,
  edgeCenterX: 0,
  edgeCenterY: 0,
  targetId: '',
  sourceHandleId: '',
  targetHandleId: '',
};

export { EdgeButton };
